import { memo, useMemo } from 'react'
import { Button } from '../../../shared/components/ui'
import type { Announcement } from '../types'

interface AnnouncementCardProps {
    announcement: Announcement
    onEdit?: (announcement: Announcement) => void
    onDelete?: (id: string) => void
    onMarkAsRead?: (id: string) => void
    currentUserId?: string
    isReadOnly?: boolean
}

const AnnouncementCard = memo<AnnouncementCardProps>(({
    announcement,
    onEdit,
    onDelete,
    onMarkAsRead,
    currentUserId,
    isReadOnly = false
}) => {
    // Memoized priority styling
    const priorityClasses = useMemo(() => {
        const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
        switch (announcement.priority) {
            case 'urgent':
                return `${baseClasses} bg-red-100 text-red-800 border border-red-200`
            case 'high':
                return `${baseClasses} bg-orange-100 text-orange-800 border border-orange-200`
            case 'medium':
                return `${baseClasses} bg-yellow-100 text-yellow-800 border border-yellow-200`
            case 'low':
                return `${baseClasses} bg-green-100 text-green-800 border border-green-200`
            default:
                return `${baseClasses} bg-gray-100 text-gray-800 border border-gray-200`
        }
    }, [announcement.priority])

    // Memoized read status
    const isRead = useMemo(() =>
        currentUserId ? announcement.readBy.includes(currentUserId) : false,
        [announcement.readBy, currentUserId]
    )

    // Memoized formatted date
    const formattedDate = useMemo(() =>
        new Date(announcement.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }),
        [announcement.createdAt]
    )

    // Memoized expiration status
    const isExpired = useMemo(() => {
        if (!announcement.expirationDate) return false
        return new Date(announcement.expirationDate) < new Date()
    }, [announcement.expirationDate])

    // Memoized card classes
    const cardClasses = useMemo(() => {
        let classes = 'bg-white rounded-lg border shadow-sm p-6 transition-all duration-200 hover:shadow-md'

        if (!announcement.isActive || isExpired) {
            classes += ' opacity-60 border-gray-200'
        } else if (!isRead) {
            classes += ' border-l-4 border-l-blue-500 bg-blue-50/30'
        } else {
            classes += ' border-gray-200'
        }

        return classes
    }, [announcement.isActive, isExpired, isRead])

    const handleEdit = () => {
        if (onEdit) onEdit(announcement)
    }

    const handleDelete = () => {
        if (onDelete && window.confirm('Are you sure you want to delete this announcement?')) {
            onDelete(announcement.id)
        }
    }

    const handleMarkAsRead = () => {
        if (onMarkAsRead && !isRead) {
            onMarkAsRead(announcement.id)
        }
    }

    return (
        <article
            className={`${cardClasses} focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2`}
            role="article"
            aria-labelledby={`announcement-${announcement.id}-title`}
        >
            {/* Header - Enhanced responsive design */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <h3
                        id={`announcement-${announcement.id}-title`}
                        className="text-lg font-semibold text-gray-900 break-words"
                    >
                        {announcement.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`${priorityClasses} high-contrast:border high-contrast:border-gray-600`}
                            aria-label={`Priority: ${announcement.priority}`}
                        >
                            {announcement.priority.toUpperCase()}
                        </span>
                        {!announcement.isActive && (
                            <span
                                className="
                  inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                  bg-gray-100 text-gray-600 border border-gray-200
                  high-contrast:bg-gray-200 high-contrast:text-gray-900 high-contrast:border-gray-600
                "
                                aria-label="Status: Inactive"
                            >
                                INACTIVE
                            </span>
                        )}
                        {isExpired && (
                            <span
                                className="
                  inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                  bg-red-100 text-red-600 border border-red-200
                  high-contrast:bg-red-200 high-contrast:text-red-900 high-contrast:border-red-600
                "
                                aria-label="Status: Expired"
                            >
                                EXPIRED
                            </span>
                        )}
                    </div>
                </div>

                {!isRead && currentUserId && (
                    <div
                        className="
              w-3 h-3 bg-blue-500 rounded-full flex-shrink-0 self-start sm:self-center
              high-contrast:border-2 high-contrast:border-blue-800
            "
                        role="img"
                        aria-label="Unread announcement"
                        title="Unread announcement"
                    />
                )}
            </div>

            {/* Content */}
            <div className="mb-4">
                <p className="text-gray-700 text-sm leading-relaxed line-clamp-3">
                    {announcement.content}
                </p>
            </div>

            {/* Metadata */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <div className="flex items-center gap-4">
                    <span>Created: {formattedDate}</span>
                    {announcement.expirationDate && (
                        <span>
                            Expires: {new Date(announcement.expirationDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span>{announcement.readBy.length} read</span>
                    {announcement.targetHouses.length > 0 && (
                        <span>• {announcement.targetHouses.length} house(s)</span>
                    )}
                </div>
            </div>

            {/* Actions - Enhanced responsive design and accessibility */}
            {!isReadOnly && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                        {!isRead && currentUserId && onMarkAsRead && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleMarkAsRead}
                                className="
                  text-xs min-h-[44px] sm:min-h-[32px]
                  focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                "
                                aria-label={`Mark "${announcement.title}" as read`}
                            >
                                Mark as Read
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {onEdit && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleEdit}
                                className="
                  text-xs min-h-[44px] sm:min-h-[32px]
                  focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                "
                                aria-label={`Edit "${announcement.title}"`}
                            >
                                Edit
                            </Button>
                        )}
                        {onDelete && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDelete}
                                className="
                  text-xs text-red-600 hover:text-red-700 hover:bg-red-50
                  min-h-[44px] sm:min-h-[32px]
                  focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                  high-contrast:border high-contrast:border-red-600
                "
                                aria-label={`Delete "${announcement.title}"`}
                            >
                                Delete
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </article>
    )
})

AnnouncementCard.displayName = 'AnnouncementCard'

export default AnnouncementCard